import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Building2, Globe, LayoutDashboard, LogOut, Menu, PenLine, Send, Tags, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orgApi, platformApi } from '../lib/api.ts';
import { authClient } from '../lib/auth-client.ts';
import { LogoMark, Wordmark } from './ui.tsx';

/**
 * Marco común del panel: barra lateral de tinta y contenido sobre papel.
 *
 * La navegación es lateral y no superior porque esta lista va a crecer —Equipo,
 * Auditoría, Ajustes (§10)— y una fila horizontal se queda sin sitio al cuarto
 * elemento en un portátil.
 *
 * Solo muestra lo que existe. Enseñar "Calendario" en gris con un "próximamente"
 * llena la interfaz de promesas; es más honesto que aparezca cuando funcione.
 *
 * En móvil la barra se pliega tras un botón (§10: se usa desde el celular para
 * aprobar y reintentar).
 */
export function AppShell({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);

  // Al navegar se cierra el menú móvil. Sin esto, tras elegir una sección el
  // panel seguiría tapado por la barra que se acaba de usar. El efecto depende
  // de la ruta a propósito aunque no la lea.
  // biome-ignore lint/correctness/useExhaustiveDependencies: se dispara al cambiar de ruta
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => (await authClient.getSession()).data,
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => (await authClient.organization.list()).data ?? [],
  });

  // Solo decide si se enseña el enlace. Quien no administra la plataforma y
  // escriba la dirección a mano se encuentra con que el servidor le dice que no.
  const { data: platform } = useQuery({
    queryKey: ['platform', 'me'],
    queryFn: platformApi.me,
    staleTime: 5 * 60_000,
  });

  // Mismo `queryKey` que la pantalla de Sitios: una sola petición para las dos.
  const { data: plan } = useQuery({ queryKey: ['org', 'limits'], queryFn: orgApi.limits, retry: false });

  const active = organizations?.find((org) => org.id === session?.session.activeOrganizationId);

  const nav = [
    { to: '/', label: t('nav.dashboard'), icon: LayoutDashboard },
    { to: '/redactar', label: t('nav.compose'), icon: PenLine },
    { to: '/publicaciones', label: t('nav.publications'), icon: Send },
    { to: '/sitios', label: t('nav.sites'), icon: Globe },
    { to: '/categorias', label: t('nav.sections'), icon: Tags },
    ...(platform?.isPlatformAdmin
      ? [{ to: '/clientes', label: t('nav.clients'), icon: Building2 } as const]
      : []),
  ];

  // "Publicaciones" tiene que seguir marcada dentro del detalle de una; el
  // inicio solo con coincidencia exacta, o estaría marcado siempre.
  const isCurrent = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  const userName = session?.user.name ?? '';
  const initials = userName
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]">
      {/* --- Barra superior, solo en móvil ---------------------------------- */}
      <div className="sticky top-0 z-30 flex items-center justify-between bg-[var(--color-ink)] px-4 py-3 text-[var(--color-on-ink)] lg:hidden">
        <div className="flex items-center gap-2.5">
          <LogoMark size={28} tone="on-ink" />
          <Wordmark className="text-base" />
        </div>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="navegacion"
          aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          className="rounded-lg p-1.5 hover:bg-[var(--color-ink-soft)]"
        >
          {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
        </button>
      </div>

      {/* --- Barra lateral --------------------------------------------------- */}
      <aside
        id="navegacion"
        className={`${
          menuOpen ? 'flex' : 'hidden'
        } fixed inset-x-0 top-[3.25rem] bottom-0 z-20 flex-col bg-[var(--color-ink)] text-[var(--color-on-ink)] lg:sticky lg:top-0 lg:flex lg:h-screen`}
      >
        <div className="hidden items-center gap-3 px-5 pt-6 pb-2 lg:flex">
          <LogoMark size={32} tone="on-ink" />
          <Wordmark className="text-lg" />
        </div>

        {active ? (
          <p className="truncate px-5 pt-4 pb-1 text-xs text-[var(--color-on-ink-muted)] lg:pt-1">
            {active.name}
          </p>
        ) : null}

        <nav aria-label={t('nav.main')} className="mt-4 flex flex-1 flex-col gap-0.5 px-3">
          {nav.map((item) => {
            const current = isCurrent(item.to);
            const Icon = item.icon;

            return (
              <Link
                key={item.to}
                to={item.to}
                aria-current={current ? 'page' : undefined}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition ${
                  current
                    ? 'bg-[var(--color-brand)] font-medium text-white'
                    : 'text-[var(--color-on-ink-muted)] hover:bg-[var(--color-ink-soft)] hover:text-[var(--color-on-ink)]'
                }`}
              >
                <Icon size={18} aria-hidden="true" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 border-t border-[var(--color-ink-line)] px-4 py-4">
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-ink-soft)] text-xs font-medium"
          >
            {initials}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm">{userName}</span>
          <button
            type="button"
            onClick={async () => {
              await authClient.signOut();
              await navigate({ to: '/entrar' });
            }}
            aria-label={t('common.signOut')}
            title={t('common.signOut')}
            className="rounded-lg p-2 text-[var(--color-on-ink-muted)] hover:bg-[var(--color-ink-soft)] hover:text-[var(--color-on-ink)]"
          >
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>
      </aside>

      {/* --- Contenido ------------------------------------------------------- */}
      <main className="min-w-0 px-5 py-8 sm:px-8 lg:px-10 lg:py-10">
        <div className="mx-auto max-w-6xl">
          {/* Arriba del todo y en todas las pantallas: sin esto, quien tiene la
              cuenta suspendida lo descubre al fallarle una publicación. */}
          {plan?.suspended ? (
            <p
              role="alert"
              className="mb-6 rounded-lg border border-[var(--color-danger)]/20 bg-[var(--color-danger-soft)] px-4 py-3 text-sm leading-relaxed text-[var(--color-danger)]"
            >
              {t('nav.suspended')}
            </p>
          ) : null}

          <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight">{title}</h1>
              {description ? (
                <p className="mt-1.5 max-w-prose text-sm leading-relaxed text-[var(--color-muted)]">
                  {description}
                </p>
              ) : null}
            </div>
            {actions ? <div className="shrink-0">{actions}</div> : null}
          </header>

          {children}
        </div>
      </main>
    </div>
  );
}
